import {
  AddressChangeConsequences,
  AddressChangeReview,
  SameAcceptedWorkReview,
  needsSameAcceptedWorkReview,
} from "@/features/address-change-review";
import { Fragment, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Inbox, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import {
  type CarrierChangeRequest,
  useCarrierChangeRequests,
  useCarrierShipment,
  useHandleCarrierChangeRequest,
} from "@/api/carrier";
import { TablePager, usePagedState } from "@/components/data-table";
import { DetailDrawer } from "@/components/detail-drawer";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { NativeSelect } from "@/components/ui/native-select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { hasHttpStatus } from "@/lib/api-error";
import { formatDate } from "@/lib/format";

const COLUMNS = 7;
const UNIT_ALREADY_MATCHES_MESSAGE =
  "The requested Unit already matches the shipment. No update is needed.";
const STATUS_FILTERS = [
  { value: "PENDING", label: "Pending / applying" },
  { value: "ACTIONED", label: "Actioned" },
  { value: "CANNOT_FULFILL", label: "Cannot fulfill" },
  { value: "ALL", label: "All statuses" },
];

type Decision = {
  id: string;
  label: string;
  status: "ACTIONED" | "CANNOT_FULFILL";
} | null;

function canMarkActioned(request: CarrierChangeRequest): boolean {
  return !request.requestTypes.some(
    (type) => type === "DELIVERY_RESCHEDULE" || type === "PICKUP_RESCHEDULE"
  );
}

function detailFields(row: CarrierChangeRequest): Array<[string, string]> {
  const pairs: Array<[string, string | null]> = [
    ["Request ID", row.id],
    ["Shipment", `SHP-${row.shipmentNumber}`],
    ["Shipment ID", row.shipmentId],
    ["Tracking", row.trackingNumber],
    ["Submitted by", row.submittedBy.name],
    ["Submitter role", row.submittedBy.type ?? "CUSTOMER"],
    ["Submitter email", row.submittedBy.email],
    ["Status", row.status],
    ["Request types", row.requestTypes.join(", ") || null],
    ["Delivery instructions", row.deliveryInstructions],
    ["Address correction", row.addressCorrection],
    [
      "Requested unit",
      row.addressLine2Correction == null
        ? null
        : row.addressLine2Correction || "Remove unit",
    ],
    [
      "Reschedule date",
      row.rescheduleDate ? formatDate(row.rescheduleDate) : null,
    ],
    ["Reschedule preference", row.reschedulePreference],
    ["Contact name", row.contactName],
    ["Contact phone", row.contactPhone],
    ["Contact email", row.contactEmail],
    ["Additional notes", row.additionalNotes],
    ["Response", row.adminResponse],
    ["Applied", row.appliedChanges?.join(", ") || null],
    ["Created", formatDate(row.createdAt)],
    ["Handled", row.handledAt ? formatDate(row.handledAt) : null],
    ["Handled by", row.handledBy],
  ];
  return pairs.map(([label, value]) => [label, value ?? "-"]);
}

function ChangeRequestDetail({
  request,
  onOpenChange,
  onDecision,
}: {
  request: CarrierChangeRequest | null;
  onOpenChange: (open: boolean) => void;
  onDecision: (decision: NonNullable<Decision>) => void;
}) {
  const shipment = useCarrierShipment(request?.shipmentId);
  const destination = shipment.data?.addresses.find(
    (address) => address.type === "TO"
  );
  const sender = shipment.data?.addresses.find(
    (address) => address.type === "FROM"
  );
  const currentAddress = destination
    ? [
        destination.addressLine1,
        destination.addressLine2,
        destination.addressCity,
        destination.addressProvince,
        destination.addressPostalCode,
      ]
        .filter(Boolean)
        .join(", ") || "-"
    : "-";
  const currentContact = destination
    ? [destination.contactName, destination.contactPhone]
        .filter(Boolean)
        .join(" · ") || "-"
    : "-";
  const currentContactEmail = destination?.contactEmail ?? "-";
  const currentInstructions = shipment.data?.deliveryNotes?.trim() || "-";

  return (
    <DetailDrawer
      open={request !== null}
      onOpenChange={onOpenChange}
      title={request ? `Change request · SHP-${request.shipmentNumber}` : ""}
      description={request?.submittedBy.name}
      widthClassName="sm:max-w-xl"
      footer={
        request?.status === "PENDING" || request?.status === "ACKNOWLEDGED" ? (
          <div className="grid gap-2 sm:grid-cols-2">
            <Button
              type="button"
              variant="outline"
              size="touch"
              onClick={() =>
                onDecision({
                  id: request.id,
                  label: `SHP-${request.shipmentNumber}`,
                  status: "CANNOT_FULFILL",
                })
              }
            >
              Cannot fulfill
            </Button>
            {canMarkActioned(request) && (
              <Button
                type="button"
                size="touch"
                onClick={() =>
                  onDecision({
                    id: request.id,
                    label: `SHP-${request.shipmentNumber}`,
                    status: "ACTIONED",
                  })
                }
              >
                Apply change
              </Button>
            )}
          </div>
        ) : undefined
      }
    >
      {request && (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-1">
            <StatusBadge status={request.status} />
            {request.requestTypes.map((type) => (
              <StatusBadge key={type} status={type} />
            ))}
          </div>
          {request.addressChange && (
            <AddressChangeConsequences
              change={request.addressChange}
              shipmentId={request.shipmentId}
            />
          )}
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">Current Shipment values</h3>
            <dl className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-x-4 gap-y-2 text-sm">
              {[
                [
                  "Order",
                  shipment.data?.order
                    ? `ORD-${shipment.data.order.orderNumber}`
                    : "-",
                ],
                [
                  "Customer",
                  shipment.data?.order?.placerCustomer
                    ? `Customer #${shipment.data.order.placerCustomer.customerNumber}`
                    : "-",
                ],
                [
                  "Delivery date",
                  shipment.data?.actualDelivery ||
                  shipment.data?.estimatedDelivery ||
                  shipment.data?.shipDate
                    ? formatDate(
                        shipment.data.actualDelivery ??
                          shipment.data.estimatedDelivery ??
                          shipment.data.shipDate
                      )
                    : "-",
                ],
                ["Destination address", currentAddress],
                [
                  "Current unit",
                  destination
                    ? (destination.addressLine2 ??
                      destination.address?.line2 ??
                      "None")
                    : "-",
                ],
                ...(request.requestTypes.includes("SENDER_ADDRESS_CORRECTION")
                  ? [
                      [
                        "Current sender unit",
                        sender
                          ? (sender.addressLine2 ??
                            sender.address?.line2 ??
                            "None")
                          : "-",
                      ],
                    ]
                  : []),
                ["Recipient contact", currentContact],
                ["Recipient email", currentContactEmail],
                ["Delivery instructions", currentInstructions],
              ].map(([label, value]) => (
                <Fragment key={label}>
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="min-w-0 break-words">{value}</dd>
                </Fragment>
              ))}
            </dl>
            {shipment.isLoading && (
              <p role="status" className="text-sm text-muted-foreground">
                Loading current Shipment values…
              </p>
            )}
            {shipment.error && (
              <p role="alert" className="text-sm text-amber-800">
                Current Shipment values could not be loaded. Proposed request
                values remain available.
              </p>
            )}
          </section>
          <section className="space-y-2 border-t pt-4">
            <h3 className="text-sm font-semibold">Proposed request values</h3>
            <dl className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-x-4 gap-y-2 text-sm">
              {detailFields(request).map(([label, value]) => (
                <Fragment key={label}>
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="min-w-0 break-words">{value}</dd>
                </Fragment>
              ))}
            </dl>
          </section>
          {(request.addressLine2Correction != null ||
            request.requestTypes.includes(
              "RECIPIENT_DELIVERY_INSTRUCTIONS"
            )) && (
            <p className="text-sm text-amber-800">
              Existing labels are not updated automatically. Check the label
              before the next handoff.
            </p>
          )}
          {request.status === "ACTIONED" && !request.appliedChanges?.length && (
            <p role="status" className="text-sm text-amber-800">
              Historical request marked handled without a recorded applied
              change. Verify the current Shipment before relying on it.
            </p>
          )}
          {request.status === "PENDING" && !canMarkActioned(request) && (
            <p role="status" className="text-sm text-amber-800">
              This request needs manual support review before it can be applied.
              The current address has not changed.
            </p>
          )}
        </div>
      )}
    </DetailDrawer>
  );
}

function ChangeRequestDecisionSheet({
  decision,
  request,
  onClose,
}: {
  decision: Decision;
  request?: CarrierChangeRequest;
  onClose: () => void;
}) {
  const handle = useHandleCarrierChangeRequest();
  const [response, setResponse] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lock = useRef(false);
  const [unitAlreadyMatches, setUnitAlreadyMatches] = useState(false);
  const confirmationKey = JSON.stringify([decision, request]);
  const [confirmedKey, setConfirmedKey] = useState<string | null>(null);
  const needsWorkReview =
    decision?.status === "ACTIONED" &&
    Boolean(request && needsSameAcceptedWorkReview(request));
  const sameAcceptedWorkConfirmed = confirmedKey === confirmationKey;
  useEffect(() => {
    setConfirmedKey((current) =>
      current === confirmationKey ? current : null
    );
  }, [confirmationKey]);
  const responseRequired = decision?.status === "CANNOT_FULFILL";
  const formId = decision
    ? `change-request-decision-${decision.id}-${decision.status}`
    : "change-request-decision";

  const saveDecision = async (
    status: "ACTIONED" | "CANNOT_FULFILL",
    adminResponse?: string
  ) => {
    if (
      !decision ||
      lock.current ||
      (status === "ACTIONED" && needsWorkReview && !sameAcceptedWorkConfirmed)
    )
      return;

    lock.current = true;
    setSaving(true);
    setError(null);
    try {
      await handle.mutateAsync({
        id: decision.id,
        status,
        adminResponse,
        ...(status === "ACTIONED" && needsWorkReview
          ? { sameAcceptedWorkConfirmed: true }
          : {}),
      });
      toast.success(
        status === "ACTIONED"
          ? "Change applied and request actioned."
          : "Change request marked cannot fulfill."
      );
      onClose();
    } catch (caught) {
      const response = caught as {
        response?: { data?: { message?: string; error?: string } };
      };
      const alreadyMatches =
        status === "ACTIONED" &&
        response.response?.data?.error === "SHIPMENT_UNIT_ALREADY_MATCHES";
      const message = alreadyMatches
        ? UNIT_ALREADY_MATCHES_MESSAGE
        : hasHttpStatus(caught, 403)
          ? "You do not have permission to decide this request."
          : (response.response?.data?.message ??
            response.response?.data?.error ??
            "Change request could not be updated.");
      if (alreadyMatches) {
        setUnitAlreadyMatches(true);
      }
      setError(message);
      if (alreadyMatches) toast.info(message);
      else toast.error(message);
    } finally {
      lock.current = false;
      setSaving(false);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!decision || unitAlreadyMatches) return;
    if (responseRequired && !response.trim()) {
      setError("Explain why this request cannot be fulfilled.");
      return;
    }
    await saveDecision(decision.status, response.trim() || undefined);
  };

  if (
    decision?.status === "ACTIONED" &&
    request &&
    (request.addressChange ||
      request.requestTypes.some(
        (type) =>
          type.endsWith("ADDRESS_CORRECTION") ||
          type === "PACKAGE_DECLARATION_AMENDMENT"
      ))
  ) {
    return (
      <DetailDrawer
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        title={`Review address correction · ${decision.label}`}
        description="Approval does not release work until the checklist is complete."
        widthClassName="sm:max-w-3xl"
      >
        <AddressChangeReview
          key={request.id}
          request={request}
          endpoint={`${import.meta.env.VITE_API_BASE_URL ?? ""}/api/v2/carrier/change-requests/${request.id}`}
          isAdmin={false}
          onFinished={(state) => {
            toast.success(
              state === "APPLIED"
                ? "Address correction applied"
                : state === "RECONCILED_FOR_CANCELLATION"
                  ? "Correction reconciled; cancellation review remains pending"
                  : "Fresh correction review created"
            );
            onClose();
          }}
        />
      </DetailDrawer>
    );
  }

  return (
    <DetailDrawer
      open={decision !== null}
      onOpenChange={(open) => {
        if (!open && !saving) onClose();
      }}
      title={
        unitAlreadyMatches
          ? `Close request · ${decision?.label ?? ""}`
          : decision?.status === "ACTIONED"
            ? `Apply change · ${decision.label}`
            : `Cannot fulfill ${decision?.label ?? ""}`
      }
      description={
        unitAlreadyMatches
          ? "Close this request because the shipment already has the requested Unit."
          : "Apply the requested change and record the decision."
      }
      widthClassName="sm:max-w-md"
      closeDisabled={saving}
      footer={
        decision ? (
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              size="touch"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            {unitAlreadyMatches ? (
              <Button
                type="button"
                size="touch"
                disabled={saving}
                onClick={() =>
                  saveDecision("CANNOT_FULFILL", UNIT_ALREADY_MATCHES_MESSAGE)
                }
              >
                {saving ? "Saving…" : "Close request"}
              </Button>
            ) : (
              <Button
                type="submit"
                form={formId}
                size="touch"
                disabled={
                  saving || (needsWorkReview && !sameAcceptedWorkConfirmed)
                }
              >
                {saving ? "Saving…" : "Confirm decision"}
              </Button>
            )}
          </div>
        ) : undefined
      }
    >
      <form
        id={formId}
        onSubmit={submit}
        className="space-y-4"
        aria-busy={saving || undefined}
      >
        {needsWorkReview && request && (
          <SameAcceptedWorkReview
            request={request}
            checked={sameAcceptedWorkConfirmed}
            onChange={(checked) =>
              setConfirmedKey(checked ? confirmationKey : null)
            }
          />
        )}
        {!unitAlreadyMatches && (
          <div className="space-y-1">
            <label
              htmlFor="change-request-response"
              className="text-sm font-medium"
            >
              Response{responseRequired ? " *" : " (optional)"}
            </label>
            <Textarea
              id="change-request-response"
              rows={5}
              value={response}
              onChange={(event) => {
                setResponse(event.target.value);
                setError(null);
              }}
              disabled={saving}
            />
          </div>
        )}
        {error && (
          <p
            role={error === UNIT_ALREADY_MATCHES_MESSAGE ? "status" : "alert"}
            className={
              error === UNIT_ALREADY_MATCHES_MESSAGE
                ? "text-sm text-muted-foreground"
                : "text-sm text-destructive"
            }
          >
            {error}
          </p>
        )}
        {saving && (
          <p role="status" className="text-sm text-muted-foreground">
            Saving decision…
          </p>
        )}
      </form>
    </DetailDrawer>
  );
}

function QueueMessage({
  kind,
  filtered,
  onRetry,
}: {
  kind: "empty" | "error" | "forbidden";
  filtered: boolean;
  onRetry: () => void;
}) {
  const message =
    kind === "forbidden"
      ? "You do not have permission to view change requests."
      : kind === "error"
        ? "Change requests could not be loaded."
        : filtered
          ? "No change requests match this status."
          : "No pending change requests.";
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
      {kind === "empty" ? (
        <Inbox className="h-7 w-7 text-muted-foreground" />
      ) : (
        <TriangleAlert className="h-7 w-7 text-destructive" />
      )}
      <p role={kind === "empty" ? undefined : "alert"} className="text-sm">
        {message}
      </p>
      {kind === "error" && (
        <Button type="button" variant="outline" size="touch" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function ChangeRequestsPanel() {
  const { page, pageSize, setPage, setPageSize, resetPage } = usePagedState();
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const { data, isLoading, error, refetch } = useCarrierChangeRequests({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    status: statusFilter,
  });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [detailId, setDetailId] = useState<string | null>(null);
  const [decision, setDecision] = useState<Decision>(null);

  const rows = data?.requests ?? [];
  const detail = rows.find((row) => row.id === detailId) ?? null;
  const total = data?.pagination.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const blockingError = error && rows.length === 0;

  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const openDecision = (next: NonNullable<Decision>) => {
    setDetailId(null);
    setTimeout(() => setDecision(next), 0);
  };

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <div className="border-b p-3 sm:flex sm:justify-end">
          <NativeSelect
            aria-label="Filter by status"
            value={statusFilter}
            density="compact"
            onChange={(event) => {
              resetPage();
              setStatusFilter(event.target.value);
            }}
            className="w-full sm:w-48"
          >
            {STATUS_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </NativeSelect>
        </div>

        {error && rows.length > 0 && (
          <div
            role="alert"
            className="border-b border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
          >
            Refresh failed. Loaded requests remain available.
          </div>
        )}

        {isLoading && rows.length === 0 ? (
          <div
            role="status"
            aria-label="Loading change requests"
            className="space-y-2 p-3"
          >
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
          </div>
        ) : blockingError ? (
          <QueueMessage
            kind={hasHttpStatus(error, 403) ? "forbidden" : "error"}
            filtered={statusFilter !== "PENDING"}
            onRetry={() => void refetch()}
          />
        ) : rows.length === 0 ? (
          <QueueMessage
            kind="empty"
            filtered={statusFilter !== "PENDING"}
            onRetry={() => void refetch()}
          />
        ) : (
          <>
            <div className="space-y-2 p-3 md:hidden">
              {rows.map((row) => (
                <article
                  key={row.id}
                  className="relative rounded-md border p-3"
                >
                  <Button
                    type="button"
                    variant="ghost"
                    aria-label={`Review change request for SHP-${row.shipmentNumber}`}
                    className="absolute inset-0 z-0 h-full w-full rounded-md p-0"
                    onClick={() => setDetailId(row.id)}
                  />
                  <div className="pointer-events-none relative z-10 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-mono text-xs font-semibold">
                        SHP-{row.shipmentNumber}
                      </h3>
                      <p className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">
                        {row.trackingNumber ?? "No tracking"}
                      </p>
                    </div>
                    <StatusBadge status={row.status} />
                  </div>
                  <p className="pointer-events-none relative z-10 mt-2 break-words text-xs">
                    {row.submittedBy.type === "CARRIER"
                      ? "Carrier"
                      : row.submittedBy.type === "ADMIN"
                        ? "EZX"
                        : "Customer"}
                    : {row.submittedBy.name}
                    {row.submittedBy.email ? ` · ${row.submittedBy.email}` : ""}
                  </p>
                  <div className="pointer-events-none relative z-10 mt-2 flex flex-wrap gap-1">
                    {row.requestTypes.map((type) => (
                      <StatusBadge key={type} status={type} />
                    ))}
                  </div>
                  <p className="pointer-events-none relative z-10 mt-2 text-[11px] text-muted-foreground">
                    Requested {formatDate(row.createdAt)}
                  </p>
                  {(row.status === "PENDING" ||
                    row.status === "ACKNOWLEDGED") && (
                    <div className="pointer-events-none relative z-10 mt-3 border-t pt-3">
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          aria-label={`Cannot fulfill change request for SHP-${row.shipmentNumber}`}
                          size="touch"
                          className="pointer-events-auto relative z-20"
                          onClick={() =>
                            openDecision({
                              id: row.id,
                              label: `SHP-${row.shipmentNumber}`,
                              status: "CANNOT_FULFILL",
                            })
                          }
                        >
                          Cannot fulfill
                        </Button>
                        {canMarkActioned(row) && (
                          <Button
                            type="button"
                            aria-label={`Apply change request for SHP-${row.shipmentNumber}`}
                            size="touch"
                            className="pointer-events-auto relative z-20"
                            onClick={() =>
                              openDecision({
                                id: row.id,
                                label: `SHP-${row.shipmentNumber}`,
                                status: "ACTIONED",
                              })
                            }
                          >
                            Apply change
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </article>
              ))}
            </div>

            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" aria-label="Expand" />
                    <TableHead>Shipment</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Types</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => {
                    const isOpen = expanded.has(row.id);
                    return (
                      <Fragment key={row.id}>
                        <TableRow
                          className="cursor-pointer"
                          tabIndex={0}
                          aria-label={`Toggle change request for SHP-${row.shipmentNumber}`}
                          aria-expanded={isOpen}
                          onClick={() => toggle(row.id)}
                          onKeyDown={(event) => {
                            if (event.target !== event.currentTarget) return;
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              toggle(row.id);
                            }
                          }}
                        >
                          <TableCell>
                            {isOpen ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            SHP-{row.shipmentNumber}
                          </TableCell>
                          <TableCell>
                            <div>{row.submittedBy.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {row.submittedBy.email}
                            </div>
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={row.status} />
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {row.requestTypes.map((type) => (
                                <StatusBadge key={type} status={type} />
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {formatDate(row.createdAt)}
                          </TableCell>
                          <TableCell className="text-right">
                            {row.status === "PENDING" ||
                            row.status === "ACKNOWLEDGED" ? (
                              <div className="flex justify-end gap-2">
                                {canMarkActioned(row) && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    aria-label={`Apply change request for SHP-${row.shipmentNumber}`}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      openDecision({
                                        id: row.id,
                                        label: `SHP-${row.shipmentNumber}`,
                                        status: "ACTIONED",
                                      });
                                    }}
                                  >
                                    Apply change
                                  </Button>
                                )}
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  aria-label={`Cannot fulfill change request for SHP-${row.shipmentNumber}`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openDecision({
                                      id: row.id,
                                      label: `SHP-${row.shipmentNumber}`,
                                      status: "CANNOT_FULFILL",
                                    });
                                  }}
                                >
                                  Cannot fulfill
                                </Button>
                              </div>
                            ) : (
                              formatDate(row.handledAt)
                            )}
                          </TableCell>
                        </TableRow>
                        {isOpen && (
                          <TableRow>
                            <TableCell
                              colSpan={COLUMNS}
                              className="bg-muted/30"
                            >
                              <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
                                {detailFields(row).map(([label, value]) => (
                                  <Fragment key={label}>
                                    <dt className="text-muted-foreground">
                                      {label}
                                    </dt>
                                    <dd className="break-words">{value}</dd>
                                  </Fragment>
                                ))}
                              </dl>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {data && (
          <div className="border-t p-3">
            <TablePager
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              disabled={isLoading}
            />
          </div>
        )}
      </Card>

      <ChangeRequestDetail
        request={detail}
        onOpenChange={(open) => {
          if (!open) setDetailId(null);
        }}
        onDecision={openDecision}
      />
      <ChangeRequestDecisionSheet
        key={decision ? `${decision.id}:${decision.status}` : "closed"}
        decision={decision}
        request={rows.find((row) => row.id === decision?.id)}
        onClose={() => setDecision(null)}
      />
    </div>
  );
}
